import { VdToastNotificationService } from '@vend/vend.ui'
import angular, { IPromise } from 'angular'
import { ApiCustomer, ApiCustomerGroup } from '@vend/types'

import { trackError } from '@vend/utilities'
import { isGroupAllCustomersGroup } from '../functions/customer'
import { $q } from '../ng-compat/imports'
import * as customersResource from '../components/customer/customers-resource/customers-resource'
import { customerGroupsStore } from '../factories/database/customer-groups-store'
import { CustomersStore } from '../factories/database/customers-store'
import {
  ApiCustomerTax,
  CustomerTaxesStore,
} from '../factories/database/customer-taxes-store'
import { WebRegisterStatusManager } from './web-register-status-manager'

interface PartialApiCustomer extends Partial<ApiCustomer> {
  id: string
}

export class CustomerManager {
  constructor(
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly webRegisterStatusManager: WebRegisterStatusManager,
    private readonly customersStore: CustomersStore,
    private readonly customerTaxesStore: CustomerTaxesStore
  ) {
    'ngInject'
  }

  /**
   * Get the collection of all CustomerGroups ordered alphabetically by name (with the exception that the
   * All Customers group is always first).
   *
   * @method getCustomerGroups
   *
   * @return {Promise<Array<CustomerGroup>>} A promise resolved with the sorted collection of all customer
   *         groups
   */
  public getCustomerGroups() {
    return customerGroupsStore.all().then(customerGroups => {
      return customerGroups.sort(this._compareCustomerGroups.bind(this))
    })
  }

  /**
   * Check if the user has at least one customer
   * - Notice: Check customers[1] because all users have one default customer 'WALKIN'
   *
   * @method hasCustomer
   *
   * @return {Promise<Boolean>} A promise resolved with a Boolean result.
   *         True if the user has at least one customer, false otherwise
   */
  public hasCustomer() {
    return this.customersStore.all().then(customers => Boolean(customers[1]))
  }

  /**
   * Save the given customer details to the server and IndexedDB as a new customer.
   *
   * @method saveNewCustomer
   *
   * @param  customer {Customer}
   *         The customer details to save
   *
   * @return {Promise<Customer>} A promise resolved with the updated Customer object (including id, code, etc)
   *         once the new customer has been saved to the server and IndexedDB
   */
  public saveNewCustomer(customer: ApiCustomer) {
    return customersResource
      .postCustomer(customer)
      .then(createdCustomer => {
        // Temp fix the issue of POST /customers returns wrong customer_group_id
        const passedGroupId = customer.customer_group_id
        const returnedGroupId = createdCustomer.customer_group_id

        if (returnedGroupId !== passedGroupId) {
          createdCustomer.customer_group_id = passedGroupId
        }

        return this._syncCustomer(createdCustomer)
      })
      .catch(error => this._onSaveCustomerErrorNotify(error))
  }

  /**
   * Get the customer group for a given customer
   *
   * @method getCustomerGroup
   *
   * @return {Promise} Resolved with the customer group for a given customer
   */
  public getCustomerGroup(customer: ApiCustomer) {
    const groupId = customer && customer.customer_group_id
    let customerGroupPromise: IPromise<ApiCustomerGroup | void> = $q.resolve()

    if (groupId) {
      customerGroupPromise = customerGroupsStore.get(groupId)
    }

    return customerGroupPromise
  }

  /**
   * Retrieves the latest customer details from the api.
   * Dispatches a call to refresh custoemr tax details from api.
   * Returns the original customer back if the api fails to return data.
   *
   * @method refreshCustomer
   *
   * @param  customer {PartialApiCustomer}
   *
   * @return {Promise<Customer>} A promise resolved with the updated Customer object
   */
  public refreshCustomer(
    customer: PartialApiCustomer
  ): Promise<PartialApiCustomer> {
    /**
     * @todo Add an 'updating' property to the Customer object on each Sale, so we can highlight in the
     * UI that refreshing of he customer details are in progress. Also, potentially add a toast notification to alert
     * the cashier that refreshing the customer has failed.
     */
    return new Promise(resolve => {
      if (this.webRegisterStatusManager.isOnline()) {
        const retrievedCustomerRequest = customersResource
          .fetchCustomer(customer.id)
          .then(retrievedCustomer => {
            return this._syncCustomer(retrievedCustomer)
          })

        Promise.all([
          retrievedCustomerRequest,
          this._refreshCustomerTax(customer),
        ])
          .then(([latestCustomerInfo]) => resolve(latestCustomerInfo))
          .catch(error => {
            trackError('[customerManager] Failed to refresh customer', {
              data: { error },
            })
            resolve(customer)
          })
      } else {
        resolve(customer)
      }
    })
  }

  /**
   * Update the given customer details to the server and IndexedDB.
   *
   * @method updateCustomer
   *
   * @param  customer {Customer}
   *         The customer details to save
   *
   * @return {Promise<Customer>} A promise resolved with the updated Customer object (including id, code, etc)
   *         once the customer has been updated and saved to the server and IndexedDB
   */
  public updateCustomer(customer: ApiCustomer) {
    return customersResource
      .update(customer)
      .then(updatedCustomer => this._syncCustomer(updatedCustomer))
      .catch(error => this._onSaveCustomerErrorNotify(error))
  }

  /**
   * Get the latest selected customer tax and save it in IndexedDB
   *
   * @param customer
   * @return {Promise<ApiCustomerTax | null>} A promise resolved to
   * ApiCustomerTax or null if there is no tax override for the given customer
   */
  private _refreshCustomerTax(
    customer: PartialApiCustomer
  ): Promise<ApiCustomerTax | null> {
    return new Promise(resolve => {
      customersResource
        .fetchCustomerTax(customer.id)
        .then(customerTax => {
          if (customerTax.length > 0) {
            this._syncCustomerTax(customerTax[0]).then(() =>
              resolve(customerTax[0])
            )
          } else {
            resolve(null)
          }
        })
        .catch(error => {
          trackError('[customerManager] Failed to refresh customer tax', {
            data: { error },
          })
          resolve(null)
        })
    })
  }

  /**
   * Compare two CustomerGroups to determine order within a collection thereof. Ensures the All Customers
   * group is always first and groups are sorted alphabetically (case insensitive) thereafter.
   *
   * @private
   * @method _compareCustomerGroups
   *
   * @param  group1 {CustomerGroup}
   *         The first customer group to compare
   *
   * @param  group2 {CustomerGroup}
   *         The second customer group to compare
   *
   * @return {Number} -1 if the first group should be first, 1 if the second group should be first, 0 if the
   *         two groups are considered equal with respect to sort order
   */
  private _compareCustomerGroups(
    group1: ApiCustomerGroup,
    group2: ApiCustomerGroup
  ) {
    // Firstly, if either group is the All Customers group, bubble it to the top

    if (isGroupAllCustomersGroup(group1)) {
      return -1
    }

    if (isGroupAllCustomersGroup(group2)) {
      return 1
    }

    // Secondly, sort groups by alphabetically by name

    const group1Name = String(group1.name).toLowerCase()
    const group2Name = String(group2.name).toLowerCase()

    if (group1Name < group2Name) {
      return -1
    }

    if (group1Name > group2Name) {
      return 1
    }

    return 0
  }

  /**
   * Sync the given customer and related info to the customers store e.g. after
   * creating/updating that customer.
   *
   * @private
   * @method _syncCustomer
   *
   * @param  customer {Customer}
   *         The customer to sync to IndexedDB
   *
   * @return {Promise<Customer>} A promise resolved with the given Customer
   * once it has been saved to IndexedDB
   */
  private _syncCustomer(customer: ApiCustomer) {
    return this.customersStore
      .syncDataInNativePromise([customer])
      .then(() => customer)
  }

  /**
   * Sync the given customer tax info to IndexedDB
   *
   * @param customerTax {ApiCustomerTax}
   * @returns {IPromise<ApiCustomerTax>}
   */
  private _syncCustomerTax(customerTax: ApiCustomerTax) {
    return this.customerTaxesStore
      .syncDataInNativePromise([customerTax])
      .then(() => customerTax)
  }

  /**
   * Triggers an error notification if an error occurs trying to save a customer.
   *
   * @private
   * @method _onSaveCustomerErrorNotify
   *
   * @param  errorDetails {Object}
   *         The error details from the API response
   *
   * @return {Object} The error details from the API response
   */
  private _onSaveCustomerErrorNotify(errorDetails: any) {
    let message = 'Failed to save customer, please try again.'

    if (errorDetails) {
      const { field } = errorDetails?.data?.errors || {}
      const error = field && Object.keys(field)[0]

      if (
        error &&
        (error === 'postal_country_id' || error === 'physical_country_id') &&
        field[error] === 'This value is not a valid country.'
      ) {
        message = `Please select a valid country for the customer's ${error.replace(
          '_country_id',
          ''
        )} address.`
      }
    }

    this.vdToastNotificationService.negative({
      message,
    })

    return Promise.reject(errorDetails)
  }
}

/**
 * Manages customers and customer groups.
 *
 * @module webregister.managers.customerManager
 */
angular
  .module('webregister.managers')
  .factory('customerManager', CustomerManager)
